import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, requestWithoutContent } from "../http/httpClient";
import { readArray, readNullableInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";

/**
 * Settling and snoozing Threads in the signed-in person's own list (F44), as T3 Code does.
 * The placement is theirs alone; routes: `server/src/dmw/api/routes/threads/placements.py`.
 */

export type PlacementState = ApiSchema<"PlacementState">;
export type PlacementEvent = ApiSchema<"PlacementEvent">;
/** What a person does; `thread_woke` and `auto_settle` are module C's own. */
export type PersonPlacementEvent = Exclude<PlacementEvent, "thread_woke" | "auto_settle">;

const PLACEMENT_STATES = enumMembers<PlacementState>({ active: true, settled: true, snoozed: true, woke: true });

export interface ThreadPlacement {
  readonly threadId: string;
  readonly state: PlacementState;
  /** ISO 8601, while pinned: the person's Pinned section holds it, unless it is snoozed. */
  readonly pinnedAt: string | null;
  /** While pinned and dragged into place: its place in the person's last pin order, from 0 (T3 Code's `pinOrderKey`). */
  readonly pinRank: number | null;
  /** ISO 8601, while settled. */
  readonly settledAt: string | null;
  /** ISO 8601: when the snooze began; a person's input asked for after it wakes the Thread early. */
  readonly snoozedAt: string | null;
  /** ISO 8601: when the snooze ends. */
  readonly snoozedUntil: string | null;
  /** ISO 8601, while `woke`: when the Thread woke the snooze (work started, or a person step for this person). */
  readonly wokeAt: string | null;
  /** ISO 8601: when the person last brought it back to the active list; the active order counts it. */
  readonly returnedAt: string | null;
}

function decodePlacement(body: unknown): ThreadPlacement {
  const placement = readObject(body, "Thread placement");
  return {
    threadId: readString(placement, "thread_id"),
    state: readOneOf(placement, "state", PLACEMENT_STATES),
    pinnedAt: readNullableString(placement, "pinned_at"),
    pinRank: readNullableInteger(placement, "pin_rank"),
    settledAt: readNullableString(placement, "settled_at"),
    snoozedAt: readNullableString(placement, "snoozed_at"),
    snoozedUntil: readNullableString(placement, "snoozed_until"),
    wokeAt: readNullableString(placement, "woke_at"),
    returnedAt: readNullableString(placement, "returned_at"),
  };
}

/** The Threads the person placed in the project; every other Thread is active and unpinned. */
export function listThreadPlacements(projectId: string, signal?: AbortSignal): Promise<readonly ThreadPlacement[]> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/thread-placements`,
    signal,
    decodeResponse: (body) => readArray(body, "Thread placements", decodePlacement),
  });
}

/** Settle, un-settle, snooze (until `snoozedUntil`), wake, pin or unpin a Thread in the person's list. */
export function placeThread(threadId: string, event: PersonPlacementEvent, snoozedUntil: string | null = null): Promise<ThreadPlacement> {
  const body = { event, snoozed_until: snoozedUntil } satisfies ApiSchema<"PlaceThreadBody">;
  return requestJson({
    method: "POST",
    path: `/threads/${encodeURIComponent(threadId)}/placement`,
    body: jsonBody(body),
    decodeResponse: decodePlacement,
  });
}

/**
 * The person's pinned Threads in the project in a new order, in one request. Module C gives
 * the places and skips a Thread no longer pinned, so a stale tab can't re-pin one.
 */
export function orderPinnedThreads(projectId: string, threadIds: readonly string[]): Promise<void> {
  const body = { thread_ids: [...threadIds] } satisfies ApiSchema<"PinOrderBody">;
  return requestWithoutContent({
    method: "PUT",
    path: `/projects/${encodeURIComponent(projectId)}/thread-placements/pin-order`,
    body: jsonBody(body),
  });
}
